import { Button, Space, Typography } from "antd";
import i18n from "@/i18n";
import { FC, useCallback, useContext, useMemo } from "react";
import { Inline, View, useAppContext, useInlineContext } from "@data-root/react";
import { SchemaItemProps, useField, CustomButton } from "@data-root/react";
import { RefSchemaItem, RowWithEmbeddedMetadata, View as IView } from "@data-root/types";

import { DeckRows } from "@data-root/plugin-view-deck";
import { RefInputContext } from ".";
import { ReloadOutlined } from "@ant-design/icons";
import { v4 } from "uuid";
import { Remarks } from "../../remarks";

const { Title } = Typography;

export const RefInputFormPresentation: FC<SchemaItemProps<RefSchemaItem>> = ({ item }) => {
  const { appSpecifier } = useAppContext();

  const { ready, onAdd, onEdit, onDelete, multiple, rows, refTable, invalidate } = useContext(RefInputContext)!;
  const { view } = useInlineContext();

  const { loaded, editable, displayName, remarks } = useField(item);

  const handleOnAdd = useCallback(async () => {
    await onAdd();
  }, [onAdd]);

  const handleOnEdit = useCallback(
    async (row: RowWithEmbeddedMetadata) => {
      await onEdit(row);
    },
    [onEdit]
  );

  const handleOnDelete = useCallback(
    async (row: RowWithEmbeddedMetadata) => {
      await onDelete(row);
    },
    [onDelete]
  );

  const actionIds = useMemo(() => {
    const actionIds: string[] = [];

    if (view.customizations && item.id in view.customizations && view.customizations[item.id].actionIds) {
      actionIds.push(...view.customizations[item.id].actionIds);
    } else {
      actionIds.push("EDIT");
      actionIds.push("DELETE");
    }

    return actionIds;
  }, [item.id, view.customizations]);

  const primaryHeader = useMemo(() => {
    if (view.customizations && item.id in view.customizations && view.customizations[item.id].primaryHeader) {
      return view.customizations[item.id].primaryHeader;
    }

    return refTable.label;
  }, [item.id, refTable.label, view.customizations]);

  const secondaryHeader = useMemo(() => {
    if (view.customizations && item.id in view.customizations && view.customizations[item.id].secondaryHeader) {
      return view.customizations[item.id].secondaryHeader;
    }

    return refTable.secondaryLabel;
  }, [item.id, refTable.secondaryLabel, view.customizations]);

  const image = useMemo(() => {
    if (view.customizations && item.id in view.customizations && view.customizations[item.id].image) {
      return view.customizations[item.id].image;
    }

    return refTable.imageLabel;
  }, [item.id, refTable.imageLabel, view.customizations]);

  const inlineView = useMemo<IView>(
    () => ({
      id: `inline:${item.ref}`,
      name: `Inline ${item.ref}`,
      description: `Inline ${item.ref}`,
      type: "Deck",
      tableId: item.ref,
      icon: "TableOutlined",
      options: {
        primaryHeader,
        secondaryHeader,
        image,
        actionIds,
      },
    }),
    [actionIds, image, item.ref, primaryHeader, secondaryHeader]
  );

  const getContext = useCallback(
    () => ({
      appId: appSpecifier.appId,
      deploymentId: appSpecifier.deploymentId,
      tableId: item.ref,
      // rowId,
      // row,
    }),
    [appSpecifier.appId, appSpecifier.deploymentId, item.ref]
  );

  const containerId = useMemo(() => v4(), []);

  return (
    <View style={{ width: "100%" }}>
      <Space>
        <Title level={5}>{displayName}</Title>
        <Button icon={<ReloadOutlined />} onClick={invalidate} />
      </Space>

      <Inline
        id={containerId}
        height={500}
        table={refTable}
        view={inlineView}
        onClick={handleOnEdit}
        onEdit={handleOnEdit}
        onDelete={handleOnDelete}
      >
        <DeckRows
          child
          containerId={containerId}
          getContext={getContext}
          loaded={loaded && ready}
          totalRows={rows.length}
          rows={rows}
          showGroups={false}
        />
      </Inline>

      <CustomButton
        onClick={handleOnAdd}
        type="primary"
        style={{
          marginTop: 10,
        }}
        disabled={!loaded || !ready || !editable}
      >
        {multiple ? i18n.t("add") : i18n.t("edit")}
      </CustomButton>

      <Remarks texts={remarks} />
    </View>
  );
};
